import { useGetAllDirectorsInCompany } from '@commenda/api';
import { SingleCombobox } from '@commenda/kakashi';
import { useMemo } from 'react';

interface AffiliateMemberPickerProps {
  /** The client whose members are listed. Mount this only once one is picked: the hook fetches
   * unconditionally. */
  clientId: number;
  selectedMemberId: number | null;
  onMemberChange: (memberId: number) => void;
}

/** Picks the person a form is previewed as, so key-person fields resolve against someone real.
 * Scoped to the chosen client, the way the admin preview scopes its director picker to the chosen
 * corporation. */
export const AffiliateMemberPicker = ({
  clientId,
  selectedMemberId,
  onMemberChange,
}: AffiliateMemberPickerProps) => {
  const { data, error, isLoading } = useGetAllDirectorsInCompany(clientId);

  const memberOptions = useMemo(
    () =>
      (data?.directors ?? [])
        // The id is optional on the director type, and an option without one cannot be selected.
        .filter((director) => director.id !== undefined)
        .map((director) => ({
          label:
            [
              director.directorFirstName,
              director.directorMiddleName,
              director.directorLastName,
            ]
              .filter(Boolean)
              .join(' ') || `Member ${director.id}`,
          value: String(director.id),
        })),
    [data]
  );

  const getPlaceholder = () => {
    if (error) return 'Failed to load members';
    if (isLoading) return 'Loading members...';
    if (memberOptions.length === 0) return 'No members on this client';
    return 'Preview as member...';
  };

  return (
    // No spacing token reaches 224px (the scale tops out at 10rem), so the raw width stays.
    <div className="w-56 shrink-0">
      <SingleCombobox
        disabled={Boolean(error)}
        options={memberOptions}
        value={selectedMemberId === null ? '' : String(selectedMemberId)}
        setValue={(memberIdValue: string) => {
          if (memberIdValue) onMemberChange(Number(memberIdValue));
        }}
        canClearSelection={false}
        placeholder={getPlaceholder()}
        searchPlaceholder="Search members"
      />
    </div>
  );
};
